import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Tabs, Tab, Paper } from "@mui/material";
import {
  Outlet,
  useLocation,
  useMatch,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { ShippingCaseResponse } from "@pages/shippingView/types";
import { RootState } from "@store/index";
import { getVisibleDeliverySubPanels } from "@pages/shippingView/helpers/tabs-mapping";

export default function DeliveryLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { oid, sid } = useParams();
  const role = useSelector((state: RootState) => state.auth.user?.role) || "";

  const { shipping } = useOutletContext<{ shipping: ShippingCaseResponse }>();

  const visiblePanels = useMemo(
    () => getVisibleDeliverySubPanels(role),
    [role],
  );

  const tabs = useMemo(
    () =>
      visiblePanels.map((panel) => ({
        path: panel.path,
        label: panel.label,
      })),
    [visiblePanels],
  );

  const tabLabels = tabs.map((t) => t.label);

  const searchParams = new URLSearchParams(location.search);
  const hasQueryParam = searchParams.has("warehouse_shifting_id");
  const fromDepositWarehouse =
    (location.state as any)?.fromDepositWarehouse || hasQueryParam;

  const activeTab = tabs.findIndex((t) => {
    const pathSegment = `/delivery/${t.path}`;
    if (t.path === "whs-delivery-orders" && fromDepositWarehouse) {
      return false;
    }
    if (t.path === "deposit-warehouse" && fromDepositWarehouse) {
      return location.pathname.includes("/whs-delivery-orders/");
    }
    return (
      location.pathname.includes(pathSegment) &&
      (location.pathname.includes(`${pathSegment}/`) ||
        location.pathname.endsWith(pathSegment))
    );
  });
  const safeActiveTab = activeTab >= 0 ? activeTab : 0;

  const isDeliveryDetailsOrders = useMatch(
    "/offers/:oid/shipping-case/:sid/delivery/delivery-orders/:id",
  );
  const isDeliveryDetailsWhs = useMatch(
    "/offers/:oid/shipping-case/:sid/delivery/whs-delivery-orders/:id",
  );
  const isDeliveryDetails = isDeliveryDetailsOrders || isDeliveryDetailsWhs;

  const isDeliveryCreateOrders = useMatch(
    "/offers/:oid/shipping-case/:sid/delivery/delivery-orders/create",
  );
  const isDeliveryCreateWhs = useMatch(
    "/offers/:oid/shipping-case/:sid/delivery/whs-delivery-orders/create",
  );
  const isDeliveryCreate = isDeliveryCreateOrders || isDeliveryCreateWhs;

  useEffect(() => {
    if (!tabs.length || !oid || !sid) return;

    const hasVisiblePath = tabs.some((tab) => {
      const pathSegment = `/delivery/${tab.path}`;
      return (
        location.pathname.includes(pathSegment) &&
        (location.pathname.includes(`${pathSegment}/`) ||
          location.pathname.endsWith(pathSegment))
      );
    });

    if (!hasVisiblePath && !isDeliveryDetails && !isDeliveryCreate) {
      navigate(`/offers/${oid}/shipping-case/${sid}/delivery/${tabs[0].path}`, {
        replace: true,
      });
    }
  }, [
    tabs,
    location.pathname,
    navigate,
    oid,
    sid,
    isDeliveryDetails,
    isDeliveryCreate,
  ]);

  if (!tabs.length) {
    return null;
  }

  return (
    <>
      {!isDeliveryDetails && !isDeliveryCreate && (
        <Tabs
          className="cargo"
          value={safeActiveTab}
          onChange={(_, val) => navigate(tabs[val].path)}
        >
          {tabs.map((t) => (
            <Tab key={t.path} label={t.label} />
          ))}
        </Tabs>
      )}

      <Paper
        elevation={0}
        sx={{
          ...(!isDeliveryCreate && !isDeliveryDetails
            ? {
                p: "16px",
                border: "1px solid rgba(0, 0, 0, 0.12)",
                borderRadius: "0 4px 4px 4px",
              }
            : {}),
        }}
      >
        <Outlet
          context={{
            tabLabels,
            activeTab: safeActiveTab,
            shipping,
            tabId: tabs[safeActiveTab]?.path ?? tabs[0].path,
          }}
        />
      </Paper>
    </>
  );
}
